/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { Colors, Icon, IconName, Typography } from '@zapier/design-system';

import { labelStyles } from '@zapier/style-encapsulation';
import { SectionName, useAvroTracking } from '../../../observability';
import {
  MAX_WIDTH,
  MEDIA_MAX_WIDTH,
  MEDIA_MIN_WIDTH,
} from '../../../constants';
import { ICON_NAMES } from '../../../constants/icons';
import { CustomButton } from '../../../components/CustomButton';

export type Props = {
  buttonText: string;
  description: string;
  href: string;
  iconName: IconName;
  title: string;
};

const flexStyle = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
} as const;

const Styles = labelStyles('Plan', {
  root: css({
    ...flexStyle,
    height: '366px',
    padding: '40px 24px 29px 24px',
    color: Colors.neutral800,
    backgroundColor: Colors.cream,
    [MEDIA_MAX_WIDTH.large]: {
      padding: '24px',
      height: '215px',
    },
    [MEDIA_MAX_WIDTH.small]: {
      height: '243px',
    },
  }),
  heading: css({
    ...flexStyle,
    height: '90px',
    [MEDIA_MAX_WIDTH.large]: {
      alignItems: 'center',
    },
    [MEDIA_MAX_WIDTH.small]: {
      height: '80px',
    },
  }),
  body: css({
    ...flexStyle,
    minHeight: '190px',
    [MEDIA_MAX_WIDTH.large]: {
      alignItems: 'center',
      minHeight: '105px',
    },
    [MEDIA_MAX_WIDTH.small]: {
      minHeight: '122px',
    },
  }),
  planTitle: css({
    ...Typography.pageHeader7,
    [MEDIA_MAX_WIDTH.small]: {
      ...Typography.pageHeader9,
    },
    [`${MEDIA_MIN_WIDTH.large} and ${MAX_WIDTH.extraLarge}`]: {
      fontSize: '29px',
    },
  }),
  description: css({
    ...Typography.paragraph1,
    lineHeight: '28px',
    maxWidth: '436px',
    marginRight: '48px',
    marginLeft: '6px',
    [MEDIA_MAX_WIDTH.large]: {
      margin: '0',
      ...Typography.paragraph3,
      lineHeight: '22px',
      textAlign: 'center',
    },
  }),
  button: css({
    width: '100%',
    [`${MEDIA_MIN_WIDTH.small} and ${MAX_WIDTH.large}`]: {
      width: '250px',
    },
  }),
  buttonContent: css({
    display: 'flex',
    alignItems: 'center',
  }),
  buttonIcon: css({
    marginTop: '6px',
    marginLeft: '4px',
    span: {
      [MEDIA_MAX_WIDTH.large]: {
        height: '20px !important',
        width: '20px !important',
      },
    },
    svg: {
      [MEDIA_MAX_WIDTH.large]: {
        height: '20px !important',
        width: '20px !important',
      },
    },
  }),
});

export function Plan({
  iconName,
  title,
  description,
  href,
  buttonText,
}: Props) {
  const { emitCTAClickEvent } = useAvroTracking();
  const handleClick = () => {
    emitCTAClickEvent({
      href,
      section: SectionName.PlanComparisonSection,
      text: buttonText,
    });
  };

  return (
    <div css={Styles.root}>
      <div css={Styles.heading}>
        <Icon name={iconName} size={40} />
        <h3 css={Styles.planTitle}>{title}</h3>
      </div>
      <div css={Styles.body}>
        <p css={Styles.description}>{description}</p>
        <div css={Styles.button}>
          <CustomButton
            color="primary"
            href={href}
            isFullWidth={true}
            onClick={handleClick}
          >
            <div css={Styles.buttonContent}>
              {buttonText}
              <div css={Styles.buttonIcon}>
                <Icon name={ICON_NAMES.arrowBigRight} size={24} />
              </div>
            </div>
          </CustomButton>
        </div>
      </div>
    </div>
  );
}
